import React, {ReactNode, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {permissions$} from 'models/auth';
import {
    customsUsersState$,
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    sortingsChanged,
} from 'models/profile/customsUsers/table';
import {UserPermission} from 'types';
import {CustomsUserRow} from 'types/profile';

interface Props {
    configFunc: (t: TFunction, permissions?: UserPermission[]) => Array<any>;
    dataMapper: (data?: CustomsUserRow[] | undefined) => Array<any>;
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const CustomsUsersGridTable = ({
    configFunc,
    dataMapper,
    headerControls,
    exportConfig,
}: Props) => {
    const {t} = useTranslation();
    const permissions = useStore(permissions$);
    useGate(Gate);

    const {filters, sortings, data, isLoading} = useStore(customsUsersState$);

    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t, permissions),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings],
    );
    const mappedData = React.useMemo(() => dataMapper(data?.data), [dataMapper, data?.data, t]);

    return (
        <Table
            columns={columns}
            data={mappedData}
            totalRowsCount={data?.totalElements}
            pageCount={data?.totalPages}
            pageIndex={data?.page}
            onPageChange={pageNumberChanged}
            isLoading={isLoading}
            headerControls={headerControls}
            userConfig={{
                gridName: 'staffGrid',
                saveConfig: saveGridConfig,
                getConfig: fetchGridConfig,
            }}
            exportConfig={exportConfig}
        />
    );
};
